import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert, ListGroup, Form, HistoryPanel, StatusBadge } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { projectService } from '../../../services/projectService';
import type { ProjectHistoryEntry, ProjectHistoryDetail } from '../../../types/Project';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';

interface ProjectHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  projectId: number;
  projectName?: string;
}

const ProjectHistoryModal: React.FC<ProjectHistoryModalProps> = ({
  visible,
  onClose,
  projectId,
  projectName
}) => {
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [history, setHistory] = useState<ProjectHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canAddComment = hasPermission('project:view');

  useEffect(() => {
    if (!visible || !projectId) {
      setHistory([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    projectService
      .getProjectHistory(projectId)
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load project history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [visible, projectId]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser?.id) return;
    if (!canAddComment) return;
    try {
      setIsSubmitting(true);
      const userId = typeof currentUser.id === 'number' ? currentUser.id : parseInt(String(currentUser.id), 10);
      if (isNaN(userId)) {
        setError('Invalid user session. Please log in again.');
        return;
      }
      const updated = await projectService.addProjectHistoryComment(projectId, {
        comments: newComment.trim(),
        userId,
      });
      setHistory(updated);
      setNewComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldLabel: Record<string, string> = {
    h200ActivityType: 'Activity type',
    h200Number: 'H200 number',
    h200ContractAreaId: 'Contract area',
    h200AssetType: 'Asset type',
    h200AssetId: 'Asset (road)',
    h200StartLatitude: 'Start latitude',
    h200StartLongitude: 'Start longitude',
    h200EndLatitude: 'End latitude',
    h200EndLongitude: 'End longitude',
  };

  const formatDetail = (d: ProjectHistoryDetail): string => {
    const oldStr = d.oldValue === null || d.oldValue === undefined ? '(empty)' : String(d.oldValue);
    const newStr = d.newValue === null || d.newValue === undefined ? '(empty)' : String(d.newValue);
    const label = fieldLabel[d.field] ?? d.field;
    return `${label}: ${oldStr} → ${newStr}`;
  };

  const getHistoryActorLabel = (entry: ProjectHistoryEntry): string => {
    if (entry.user) {
      return `By ${entry.user.name} ${entry.user.lastName}`.trim() || 'By Unknown';
    }
    return 'By System';
  };

  const stageLabel: Record<string, string> = {
    created: 'Created',
    data_edit: 'Data edited',
    status_change: 'Status change',
    closed: 'Closed',
    cancelled: 'Cancelled',
    other_cost_added: 'Other cost added',
    other_cost_updated: 'Other cost updated',
    other_cost_deleted: 'Other cost deleted',
    h200_form_uploaded: 'H200 form uploaded',
    h200_form_deleted: 'H200 form deleted',
    invoice_added: 'Invoice added',
    invoice_updated: 'Invoice updated',
    invoice_deleted: 'Invoice removed',
    task_created: 'Task created',
    task_cancelled: 'Task cancelled',
    manual_comment: 'Manual Comment'
  };
  const formatStageLabel = (stage: string): string => stageLabel[stage] ?? stage;

  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={projectName ? `Project history: ${projectName}` : 'Project history'}
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <HistoryPanel
        composer={
          canAddComment ? (
            <form onSubmit={handleSubmitComment}>
              <div className="tw-mb-3">
                <Form.Label>Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  // Shorter on a phone, where the composer sits above the timeline and a
                  // six-row textarea would push the history off screen on its own.
                  rows={3}
                  placeholder="Enter your comment here..."
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                className="tw-w-full"
                disabled={!newComment.trim() || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add Comment'}
              </Button>
            </form>
          ) : (
            <Alert variant="warning">
              <i className="bi bi-exclamation-triangle tw-mr-2"></i>
              You don&apos;t have permission to add comments to project history.
            </Alert>
          )
        }
      >
        {loading ? (
          <div className="tw-text-center tw-py-4">
            <Spinner className="tw-text-primary">Loading...</Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : sortedHistory.length > 0 ? (
          <ListGroup>
            {sortedHistory.map((entry) => (
              <ListGroup.Item key={entry.id}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-min-w-0 tw-grow">
                    <h6 className="tw-mb-1">
                      {entry.summary || formatStageLabel(entry.stage)}
                    </h6>
                    <p className="tw-mb-1 tw-text-content-muted">
                      Stage: <StatusBadge status={entry.stage} label={formatStageLabel(entry.stage)} />
                    </p>
                    {entry.details && entry.details.length > 0 && (
                      <div className="tw-mb-1">
                        {entry.details.map((d, idx) => (
                          <div key={idx} className="tw-text-sm">
                            {formatDetail(d)}
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="tw-text-content-muted">{getHistoryActorLabel(entry)}</small>
                  </div>
                  <small className="tw-shrink-0 tw-text-content-muted tw-ml-2">
                    {new Date(entry.createdAt).toLocaleString()}
                  </small>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="info">No history records found.</Alert>
        )}
      </HistoryPanel>
    </ResponsiveModal>
  );
};

export default ProjectHistoryModal;
